import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PackageCheck, Printer, Loader2 } from "lucide-react";
import DateInput from "./ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtQ = (n) => Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 3 });

/**
 * Reporte "Mercaderías pendientes": por despachar (ventas con entrega posterior)
 * o por recibir (compras con entrega posterior). Solo filas con pendiente > 0
 * (o todo, según el filtro). Click en el documento abre la pantalla de entrega/recepción.
 */
export default function MercaderiasPendientesReport({ token, logoTipo }) {
  const navigate = useNavigate();
  const hoy = new Date().toISOString().slice(0, 10);
  const [tipo, setTipo] = useState("venta");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [estado, setEstado] = useState("pendientes");
  const [entidad, setEntidad] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generar = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ tipo, estado });
      if (logoTipo) p.set("logo_tipo", logoTipo);
      if (desde) p.set("desde", desde);
      if (hasta) p.set("hasta", hasta);
      const r = await fetch(`${API}/admin/reportes/mercaderias-pendientes?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || "No se pudo generar el reporte"); setData(null); }
      else setData(d);
    } catch { toast.error("Error de conexión"); }
    setLoading(false);
  };

  const filas = useMemo(() => {
    const f = data?.filas || [];
    const q = entidad.trim().toLowerCase();
    return q ? f.filter((x) => (x.entidad_nombre || "").toLowerCase().includes(q)) : f;
  }, [data, entidad]);

  const total = filas.reduce((a, f) => a + Number(f.pendiente || 0), 0);
  const L = tipo === "venta"
    ? { titulo: "Mercaderías por despachar", entidad: "Cliente", cant: "Facturada", ent: "Entregada", accion: "Generar entrega" }
    : { titulo: "Mercaderías por recibir", entidad: "Proveedor", cant: "Comprada", ent: "Recibida", accion: "Generar recepción" };

  const abrir = (f) => {
    if (tipo === "venta") navigate(`/sistema/ventas?tab=entregas&entrega_factura=${f.origen_id}`);
    else navigate(`/sistema/egresos?tab=recepcion&recibir_compra=${f.origen_id}`);
  };

  const imprimir = () => {
    const w = window.open("", "_blank", "width=900,height=900");
    if (!w) { toast.error("Habilitá las ventanas emergentes para imprimir"); return; }
    const rows = filas.map((f) => `<tr><td>${f.fecha || ""}</td><td>${f.numero || ""}</td><td>${f.entidad_nombre || ""}</td>
      <td>${f.descripcion || ""}${f.variante_texto ? " — " + f.variante_texto : ""}</td><td style="text-align:right">${fmtQ(f.facturada)}</td>
      <td style="text-align:right">${fmtQ(f.entregada)}</td><td style="text-align:right"><b>${fmtQ(f.pendiente)}</b></td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${L.titulo}</title>
      <style>body{font-family:Arial,sans-serif;font-size:11px;padding:20px}h1{font-size:16px}table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #555;padding:4px 6px}th{background:#eee;text-align:left}</style></head><body>
      <h1>${L.titulo}</h1><p>Emitido: ${hoy} ${desde || hasta ? `· Período: ${desde || "…"} a ${hasta || "…"}` : ""}</p>
      <table><thead><tr><th>Fecha</th><th>Documento</th><th>${L.entidad}</th><th>Producto</th><th>${L.cant}</th><th>${L.ent}</th><th>Pendiente</th></tr></thead>
      <tbody>${rows}<tr><td colspan="6" style="text-align:right"><b>Total pendiente</b></td><td style="text-align:right"><b>${fmtQ(total)}</b></td></tr></tbody></table>
      <script>window.onload=function(){window.print();}</script></body></html>`);
    w.document.close();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-slate-500 text-xs font-medium">Tipo</label>
          <div className="flex rounded-lg overflow-hidden border border-white/10">
            {[["venta", "Por despachar"], ["compra", "Por recibir"]].map(([v, l]) => (
              <button key={v} onClick={() => { setTipo(v); setData(null); }}
                className={`px-3 py-2 text-sm ${tipo === v ? "bg-emerald-600 text-white" : "bg-arandu-dark text-slate-300 hover:text-white"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-slate-500 text-xs font-medium">Desde</label>
          <DateInput value={desde} onChange={(e) => setDesde(e.target.value)} className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-slate-500 text-xs font-medium">Hasta</label>
          <DateInput value={hasta} onChange={(e) => setHasta(e.target.value)} className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-slate-500 text-xs font-medium">Estado</label>
          <select value={estado} onChange={(e) => setEstado(e.target.value)} className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none">
            <option value="pendientes">Solo con pendiente</option>
            <option value="todo">Todo (incluye completos)</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-slate-500 text-xs font-medium">{L.entidad} (filtro)</label>
          <input value={entidad} onChange={(e) => setEntidad(e.target.value)} placeholder="Nombre…" className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
        </div>
        <button onClick={generar} disabled={loading}
          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />} Generar reporte
        </button>
        {data && filas.length > 0 && (
          <button onClick={imprimir} className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-semibold border border-white/10">
            <Printer className="w-4 h-4" /> Imprimir
          </button>
        )}
      </div>

      {data && (
        <div className="border border-white/10 rounded-xl overflow-hidden">
          <div className="px-4 py-2 bg-white/5 text-xs text-slate-300 flex gap-4">
            <span><b className="text-white">{data.documentos}</b> documento(s)</span>
            <span>Total pendiente: <b className="text-amber-300">{fmtQ(total)}</b> unidades</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Documento</th>
                  <th className="text-left px-3 py-2">{L.entidad}</th>
                  <th className="text-left px-3 py-2">Producto</th>
                  <th className="text-right px-3 py-2">{L.cant}</th>
                  <th className="text-right px-3 py-2">{L.ent}</th>
                  <th className="text-right px-3 py-2">Pendiente</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {filas.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-6">Sin mercadería pendiente con estos filtros.</td></tr>}
                {filas.map((f, i) => (
                  <tr key={`${f.origen_id}-${f.linea_idx}-${i}`} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-3 py-2 text-slate-300">{f.fecha}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => abrir(f)} className="font-mono text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline">{f.numero || "—"}</button>
                      {f.sin_factura && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-500/20 text-slate-300">Boleta</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-200">{f.entidad_nombre}</td>
                    <td className="px-3 py-2 text-white">{f.descripcion}{f.variante_texto ? <span className="text-slate-400"> — {f.variante_texto}</span> : null}</td>
                    <td className="px-3 py-2 text-right text-slate-200">{fmtQ(f.facturada)}</td>
                    <td className="px-3 py-2 text-right text-slate-200">{fmtQ(f.entregada)}</td>
                    <td className="px-3 py-2 text-right text-amber-300 font-semibold">{fmtQ(f.pendiente)}</td>
                    <td className="px-3 py-2 text-right">
                      {f.pendiente > 0 && (
                        <button onClick={() => abrir(f)} className="text-xs px-2 py-1 rounded bg-emerald-600/80 hover:bg-emerald-500 text-white whitespace-nowrap">{L.accion}</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
